import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  createChart,
  ColorType,
  CrosshairMode,
  PriceScaleMode,
} from "lightweight-charts";
import { baseAxios } from "../../apis";

const fetchCandles = async (
  symbol,
  interval = "1d",
  endTime = null,
  limit = 1000
) => {
  if (!symbol) return [];

  let queryParams = `symbol=${symbol}&interval=${interval}&limit=${limit}`;
  if (endTime) queryParams += `&endTime=${endTime}`;

  const BINANCE_URL = `https://api.binance.com/api/v3/klines?${queryParams}`;
  const BACKEND_URL = `/api/bot_detail/chart_klines?${queryParams}`;

  let rawData;

  try {
    // Create timeout promise (3.5 seconds)
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Binance timeout")), 3500)
    );

    // Race between fetch and timeout
    const fetchPromise = fetch(BINANCE_URL).then((res) => {
      if (!res.ok) throw new Error("Binance failed");
      return res.json();
    });

    rawData = await Promise.race([fetchPromise, timeoutPromise]);
  } catch (err) {
    const { data } = await baseAxios.get(BACKEND_URL);
    rawData = data;
  }

  return rawData
    .map((d) => ({
      time: d[0] / 1000,
      open: parseFloat(d[1]),
      high: parseFloat(d[2]),
      low: parseFloat(d[3]),
      close: parseFloat(d[4]),
    }))
    .sort((a, b) => a.time - b.time);
};

// A dedicated hook for fetching Binance k-line (candlestick) data.
const useBinanceKlinesQuery = (selectedCoin, interval) => {
  const symbol = selectedCoin
    ? `${selectedCoin.symbol.toUpperCase()}USDT`
    : null;
  return useQuery({
    queryKey: ["binanceKlines", symbol, interval],
    queryFn: () => fetchCandles(symbol, interval),
    enabled: !!symbol,
    // staleTime: false,
    // refetchInterval: false,
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 5,
  });
};

const LightweightChartWithOrders = ({ selectedCoin, ordersHistory }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const [currentTime, setCurrentTime] = useState("");
  const [interval, setInterval] = useState("1d");

  const { data: chartData = [] } = useBinanceKlinesQuery(
    selectedCoin,
    interval
  );

  const initialDataLoadedRef = useRef(null);

  // --- Chart Initialization (runs only once) ---
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
      layout: {
        background: { type: ColorType.Solid, color: "#ffffff" },
        textColor: "#333",
      },
      grid: {
        vertLines: { color: "#f0f0f0" },
        horzLines: { color: "#f0f0f0" },
      },
      timeScale: { borderColor: "#ddd", timeVisible: true },
      priceScale: { borderColor: "#ddd" },
      crosshair: { mode: CrosshairMode.Normal },
    });

    const series = chart.addCandlestickSeries({
      upColor: "#26a69a",
      downColor: "#ef5350",
      borderVisible: false,
      wickUpColor: "#26a69a",
      wickDownColor: "#ef5350",
      priceFormat: { type: "price", precision: 4, minMove: 0.0001 },
    });

    chartRef.current = chart;
    seriesRef.current = series;

    const handleResize = () => {
      if (chartRef.current && chartContainerRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight,
        });
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, []);

  // --- Effect to intelligently update chart data ---
  useEffect(() => {
    if (!seriesRef.current || chartData.length === 0) return;

    const currentSymbol = selectedCoin?.symbol;
    if (initialDataLoadedRef.current !== currentSymbol) {
      seriesRef.current.setData(chartData);
      if (chartData.length > 0) {
        chartRef.current?.timeScale().fitContent();
      }
      initialDataLoadedRef.current = currentSymbol;
    } else {
      const lastCandle = chartData[chartData.length - 1];
      seriesRef.current.update(lastCandle);
    }

    if (chartRef.current && selectedCoin) {
      chartRef.current.applyOptions({
        watermark: {
          color: "rgba(200, 200, 200, 0.4)",
          visible: true,
          text: `${currentSymbol.toUpperCase()}USDT`,
          fontSize: 24,
          horzAlign: "center",
          vertAlign: "center",
        },
      });
    }
  }, [chartData, selectedCoin]);

  // --- Effect to update order MARKERS ---
  useEffect(() => {
    if (
      !seriesRef.current ||
      !ordersHistory ||
      !selectedCoin ||
      chartData.length === 0
    ) {
      seriesRef.current?.setMarkers([]);
      return;
    }

    const getOrderTimestamp = (datetimeStr) => {
      const parts = datetimeStr.split(" ")[0].split("-");
      const date = new Date(Date.UTC(`20${parts[2]}`, parts[1] - 1, parts[0]));
      return date.getTime() / 1000;
    };

    const markers = ordersHistory
      .filter((order) =>
        order.pair.startsWith(selectedCoin.symbol.toUpperCase())
      )
      .map((order) => {
        const time = getOrderTimestamp(order.datetime);
        const isBuy = order.side === "Buy";
        return {
          time: time,
          position: isBuy ? "belowBar" : "aboveBar",
          color: isBuy ? "#26a69a" : "#ef5350",
          shape: isBuy ? "arrowUp" : "arrowDown",
          text: `${order.side} @ ${parseFloat(order.price.replace(",", ""))}`,
        };
      })
      .sort((a, b) => a.time - b.time);

    seriesRef.current.setMarkers(markers);
  }, [ordersHistory, selectedCoin, chartData]);

  // --- UI Handlers and Clock ---
  useEffect(() => {
    const updateClock = () => {
      const date = new Date();
      setCurrentTime(
        date.toLocaleTimeString("en-GB", { timeZone: "UTC", hour12: false }) +
          " (UTC)"
      );
    };
    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, []);

  const setTimeRange = (range) => {
    if (!chartRef.current || chartData.length === 0) return;
    if (range === "All") {
      chartRef.current.timeScale().fitContent();
      return;
    }
    const to = chartData[chartData.length - 1].time;
    let fromDate = new Date(to * 1000);
    switch (range) {
      case "5D":
        fromDate.setDate(fromDate.getDate() - 5);
        break;
      case "1W":
        fromDate.setDate(fromDate.getDate() - 7);
        break;
      case "1M":
        fromDate.setMonth(fromDate.getMonth() - 1);
        break;
      case "6M":
        fromDate.setMonth(fromDate.getMonth() - 6);
        break;
      case "YTD":
        fromDate = new Date(new Date().getFullYear(), 0, 1);
        break;
      case "1Y":
        fromDate.setFullYear(fromDate.getFullYear() - 1);
        break;
      default:
        break;
    }
    const from = fromDate.getTime() / 1000;
    chartRef.current.timeScale().setVisibleRange({ from, to });
  };

  const setPriceScaleMode = (mode) => {
    if (chartRef.current) {
      chartRef.current.priceScale("right").applyOptions({ mode });
    }
  };

  const buttonStyle = {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "5px 10px",
    margin: "0 2px",
    fontSize: "12px",
    color: "#222",
  };

  const statusBarButtonStyle = { ...buttonStyle };

  const timeframes = [
    { label: "1m", value: "1m" },
    { label: "5m", value: "5m" },
    { label: "15m", value: "15m" },
    { label: "1h", value: "1h" },
    { label: "4h", value: "4h" },
    { label: "1d", value: "1d" },
  ];

  return (
    <div>
      <div
        ref={chartContainerRef}
        className="h-[450px] xl:h-[580px]"
        style={{ position: "relative", width: "100%" }}
      />
      <div className="flex items-center justify-between border-t mt-2">
        <div style={{ padding: "10px 0" }}>
          {timeframes.map((tf) => (
            <button
              key={tf.value}
              style={{
                ...buttonStyle,
                fontWeight: interval === tf.value ? "bold" : "normal",
                color: interval === tf.value ? "#2E60FF" : "#222",
              }}
              onClick={() => setInterval(tf.value)}
            >
              {tf.label}
            </button>
          ))}
        </div>

        <div className="flex items-center p-[8px] text-sm text-[#222]">
          <span className="inline-block border-r border-r-[#E8E8E8] pr-4">
            {currentTime}
          </span>
          <button
            style={statusBarButtonStyle}
            onClick={() => setPriceScaleMode(PriceScaleMode.Percentage)}
          >
            %
          </button>
          <button
            style={statusBarButtonStyle}
            onClick={() => setPriceScaleMode(PriceScaleMode.Logarithmic)}
          >
            log
          </button>
          <button
            style={statusBarButtonStyle}
            className="!text-[#2E60FF]"
            onClick={() => setPriceScaleMode(PriceScaleMode.Normal)}
          >
            auto
          </button>
        </div>
      </div>
    </div>
  );
};

export default LightweightChartWithOrders;
